import { Box, Modal, TextField, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import TravelingSheetPDF from './details/v1/TravelingSheetPDF'
import TimeSheetPDF from './details/v1/TimesheetPDF'
import { PrintDocument, usePrintReportData } from './details/v2/Print/Print'
import { useGetWorkTypesQuery } from '../../store/api/timesheet/timesheetWorkTypesApi'
import { useGetCompanyGratificationsQuery } from '../../store/api/timesheet/timesheetCompanyGratificationApi'
import { PDFViewer } from '@react-pdf/renderer'
import { useTimesheetRejectMutation } from '../../store/api/timesheet/timesheetApi'
import { registryKmsError } from '../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsVerificationSlice'
import { rejectApproval } from '../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'
import { approvalLoading } from '../../store/slices/function/timesheetLoaderSlice'
import { openNoApprovalModal } from '../../store/slices/timesheet/timesheetModalSlice'
import { handleLaterKmsForError } from '../../utils/error'
import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import ApprovalLoader from '../general/ApprovalLoader'
import { addDetailsDriver, handleDriver } from '../../store/slices/timesheet/timesheetPageDetailSlice'
import { handleError, handleResponse } from '../../utils/errorHandler'
import CustomButton from '../v2/general/CustomButton/CustomButton'
import { TimesheetStoreDispatch } from '../../store/timesheetStore'
import { TimesheetStoreState } from '../../store/timesheetStore'
import { useGetTimesheetQuery } from '../../utils/hooks/useGetTimesheetQuery'
import { ServicePrint, usePrintServiceReportData } from './details/v2/Print/PrintService'
import { useGetTimesheetsTotalsQuery } from '../../utils/hooks/useGetTimesheetsTotalsQuery'
import { useGetTimesheetsFilteredQuery } from '../../utils/hooks/useGetTimesheetsFilteredQuery'

const style = {
	position: 'absolute',
	display: 'flex',
	flexDirection: 'column',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: 600,
	height: 350,
	bgcolor: 'background.paper',
	boxShadow: 24,
	p: 4,
}

function ApprovalDetails(): React.JSX.Element {
	const dispatch = useDispatch<TimesheetStoreDispatch>()
	const location = useLocation()
	const params = useParams()
	const timesheetPageDetail = useSelector((state: TimesheetStoreState) => state.timesheetPageDetail)
	const timesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)
	const pdfState = useSelector((state: TimesheetStoreState) => state.pdfSlice)

	// Detectar se está na rota de print
	const isTimesheetPrintRoute = location.pathname.endsWith('/print')

	// Detect if it is in the print route of the service (URL contains ?Service)
	const isServicePrintRoute = location.pathname.includes('/print-service/')

	// Coletar dados do Redux para o PDF (fora do PDFViewer)
	const pdfData = usePrintReportData()

	// Colect data from Redux to generate the service PDF
	const pdfServiceData = usePrintServiceReportData(params.serviceId ? parseInt(params.serviceId) : 0)

	const { data: workTypes } = useGetWorkTypesQuery()
	const { data: companyGratifications } = useGetCompanyGratificationsQuery(pdfData.timesheetPage.driver.driverId)

	const noApprovalModal = useSelector((state: TimesheetStoreState) => state.timesheetNoApprovalModal)
	const timesheetApprovalDetailsReject = useSelector(
		(state: TimesheetStoreState) => state.timesheetApprovalDetailsReject,
	)
	const isApprovalLoading = useSelector((state: TimesheetStoreState) => state.approvalLoading)
	const { isLoading, isError, refetch: refetchTimesheet } = useGetTimesheetQuery()

	const { data, refetch: refetchTimesheets } = useGetTimesheetsFilteredQuery()
	const [timesheetReject] = useTimesheetRejectMutation()
	const [timesheetDriverStatus, setTimesheetDriverStatus] = useState<string>(timesheetPageDetail.driver.status)
	const { refetch: refetchTimesheetTotals } = useGetTimesheetsTotalsQuery()
	const handleCloseModal = () => dispatch(openNoApprovalModal({ isModalOpen: false }))

	useEffect(() => {
		if (data?.itemList.length !== 0 && timesheetPageDetail?.driver?.timeSheetId !== null) {
			if (!data?.itemList) return
			for (let i = 0; i <= data.itemList.length; i++) {
				if (
					data?.itemList[i]?.timeSheetId === timesheetPageDetail?.driver?.timeSheetId &&
					timesheetPageDetail?.driver?.timeSheetId !== null
				) {
					setTimesheetDriverStatus(data?.itemList[i]?.status)

					dispatch(
						addDetailsDriver({
							driverTableId: timesheetPageDetail.driverTableId,
						}),
					)
					dispatch(handleDriver({ status: data?.itemList[i]?.status }))
					break
				}
			}
		}
	}, [data])

	useEffect(() => {
		setTimeout(() => {
			if (!handleLaterKmsForError(timesheetPage.services)) {
				dispatch(
					registryKmsError({
						registryKmsError: handleLaterKmsForError(timesheetPage.services),
					}),
				)
			}
		}, 4000)
	}, [timesheetPage.services])

	const handleNoApprovalInfo = (event: React.ChangeEvent<HTMLInputElement>) => {
		dispatch(
			rejectApproval({
				...timesheetApprovalDetailsReject,
				comments: event.target.value,
			}),
		)
	}

	const handleNoApproval = () => {
		try {
			if (!isLoading || !isError) {
				dispatch(approvalLoading({ loading: true }))
				timesheetReject({ ...timesheetApprovalDetailsReject })
					.then(response => {
						handleResponse(response)
						refetchTimesheets()
						refetchTimesheet()
						refetchTimesheetTotals()

						dispatch(
							addDetailsDriver({
								driverTableId: timesheetPageDetail.driverTableId,
							}),
						)
						dispatch(handleDriver({ status: timesheetDriverStatus }))
						dispatch(approvalLoading({ loading: false }))
					})
					.catch(error => {
						handleError(error, 'ApprovalDetails.tsx')
						dispatch(approvalLoading({ loading: false }))
					})
			}
			dispatch(openNoApprovalModal({ isModalOpen: false }))
			window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
		} catch (error) {
			handleError(error, 'ApprovalDetails.tsx')
			window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
			refetchTimesheets()
		}
	}

	if (isLoading) {
		return <></>
	}

	return (
		<Box
			id='pdf-timesheet-content'
			sx={{
				borderBottomLeftRadius: '14px',
				borderBottomRightRadius: '14px',
				marginRight: 1.5,
			}}
		>
			<Box sx={{ display: 'flex', flexDirection: 'column' }}>
				{isTimesheetPrintRoute || isServicePrintRoute || pdfState.clicked || pdfState.showReport ? (
					<Box
						sx={{
							height: 'calc(100vh - 361px)',
						}}
					>
						{isTimesheetPrintRoute ? (
							<PDFViewer
								width='100%'
								height='100%'
							>
								<PrintDocument
									timesheetPage={pdfData.timesheetPage}
									servicesOriginal={pdfData.servicesOriginal}
									servicesExecution={pdfData.servicesExecution}
									servicesApproval={pdfData.servicesApproval}
									isTimesheetApproved={pdfData.isTimesheetApproved}
									workTypes={workTypes}
									companyGratifications={companyGratifications}
								/>
							</PDFViewer>
						) : (
							<PDFViewer
								width='100%'
								height='100%'
							>
								<ServicePrint
									timesheetPage={pdfServiceData.timesheetPage}
									serviceOriginal={pdfServiceData.serviceOriginal}
									serviceExecution={pdfServiceData.serviceExecution}
									serviceApproval={pdfServiceData.serviceApproval}
								/>
							</PDFViewer>
						)}
					</Box>
				) : (
					<Box sx={{ display: 'grid', gap: '16px', backgroundColor: 'var(--secondary-light)' }}>
						<TravelingSheetPDF />
						<TimeSheetPDF />
					</Box>
				)}
			</Box>
			<Modal
				// keepMounted
				open={isApprovalLoading.loading}
			>
				<ApprovalLoader />
			</Modal>
			<Modal
				// keepMounted
				open={noApprovalModal.isModalOpen}
				onClose={handleCloseModal}
			>
				<Box sx={style}>
					<Box sx={{ display: 'flex', justifyContent: 'center' }}>
						<Typography
							id='keep-mounted-modal-title'
							variant='h5'
							component='h2'
							sx={{ fontFamily: 'var(--font-family)', color: 'var(--primary-dark)' }}
						>
							Enviar Notificação
						</Typography>
					</Box>
					<Box sx={{ display: 'flex', justifyContent: 'center' }}>
						<Typography
							id='keep-mounted-modal-description'
							variant='h6'
							sx={{ mt: 2, fontFamily: 'var(--font-family)', color: 'var(--primary-dark)' }}
						>
							Pedir edição de dados ao Motorista
						</Typography>
					</Box>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-start',
							paddingInlineStart: '0.5rem',
						}}
					>
						<Typography
							id='keep-mounted-modal-description'
							sx={{ mt: 2, fontFamily: 'var(--font-family)', color: 'var(--primary-dark)' }}
						>
							Verifique o Sumário do dia
						</Typography>
					</Box>
					<TextField
						fullWidth
						sx={{ marginBlockEnd: '2rem', marginBlockStart: '0.5rem' }}
						inputProps={{ style: { paddingBlockEnd: '4rem' } }}
						label='Mensagem (500 caracteres)'
						variant='outlined'
						onChange={handleNoApprovalInfo}
					/>
					<Box sx={{ display: 'flex', justifyContent: 'center' }}>
						<CustomButton onClick={handleNoApproval}>Confirmar Envio</CustomButton>
					</Box>
				</Box>
			</Modal>
		</Box>
	)
}

export default ApprovalDetails
